import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Stethoscope, Save, Loader2, Calendar } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useT } from "@/i18n/I18nContext";

/**
 * Edits a case's treatment plan (visits/week + duration in weeks + start date).
 * Used in attorney case detail + admin lead detail.
 *
 * Props:
 *   open, onOpenChange, lead, onSaved(updatedLead)
 *   scope: 'attorney' | 'admin'  (drives the PATCH endpoint path)
 */
export default function TreatmentPlanDialog({ open, onOpenChange, lead, onSaved, scope = "attorney" }) {
  const t = useT();
  const [vpw, setVpw] = useState(lead?.treatment_plan_visits_per_week ?? "");
  const [weeks, setWeeks] = useState(lead?.treatment_plan_duration_weeks ?? "");
  const [start, setStart] = useState(
    lead?.treatment_plan_start_date || new Date().toISOString().slice(0, 10),
  );
  const [notes, setNotes] = useState(lead?.treatment_plan_notes ?? "");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!vpw || !weeks || !start) {
      toast.error(t("plan.required"));
      return;
    }
    setSaving(true);
    try {
      const path =
        scope === "admin"
          ? `/admin/leads/${lead.id}/treatment-plan`
          : `/attorney/cases/${lead.id}/treatment-plan`;
      const { data } = await api.patch(path, {
        treatment_plan_visits_per_week: parseFloat(vpw),
        treatment_plan_duration_weeks: parseInt(weeks, 10),
        treatment_plan_start_date: start,
        treatment_plan_notes: notes?.trim() || null,
      });
      onSaved?.(data);
      toast.success(t("plan.saved"));
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="treatment-plan-dialog">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2 font-display text-xl font-black text-[#11322A]">
            <Stethoscope className="h-5 w-5 text-[#E85D04]" /> {t("plan.title")}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="mt-4 space-y-4">
          <p className="text-xs text-[#5C6B68]">{t("plan.intro")}</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="label-cap">{t("plan.visits_per_week")}</label>
              <input
                type="number"
                step="0.5"
                min="0"
                max="14"
                value={vpw}
                onChange={(e) => setVpw(e.target.value)}
                className="input-field mt-2"
                placeholder="2"
                required
                data-testid="plan-vpw"
              />
            </div>
            <div>
              <label className="label-cap">{t("plan.duration_weeks")}</label>
              <input
                type="number"
                min="1"
                max="104"
                value={weeks}
                onChange={(e) => setWeeks(e.target.value)}
                className="input-field mt-2"
                placeholder="12"
                required
                data-testid="plan-weeks"
              />
            </div>
          </div>
          <div>
            <label className="label-cap">{t("plan.start_date")}</label>
            <div className="relative mt-2">
              <Calendar className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#5C6B68]" />
              <input
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                className="input-field pl-9"
                required
                data-testid="plan-start"
              />
            </div>
          </div>
          <div>
            <label className="label-cap">{t("plan.notes")}</label>
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="input-field mt-2"
              placeholder={t("plan.notes_placeholder")}
              data-testid="plan-notes"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="rounded-lg border border-[#E0E4E2] bg-white px-4 py-2 text-sm font-bold text-[#0B1317] hover:bg-[#FDFBF7]"
            >
              {t("plan.cancel")}
            </button>
            <button type="submit" disabled={saving} className="btn-primary disabled:opacity-50" data-testid="plan-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {saving ? t("plan.saving") : t("plan.save")}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
